import React, { useState, useMemo } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  ColumnDef,
  flexRender,
  SortingState,
} from '@tanstack/react-table';
import { Search, Download, ChevronLeft, ChevronRight, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { usePersistentState } from '../../lib/usePersistentState';

interface DataTableProps<TData> {
  data: TData[];
  columns: ColumnDef<TData, any>[];
  searchPlaceholder?: string;
  exportFilename?: string;
  pageSize?: number;
  actions?: React.ReactNode;
  filterComponent?: React.ReactNode;
  // When set, the search box ALSO drives a backend query (debounced) so results
  // aren't limited to the rows already loaded. Client-side filtering still runs
  // on top of whatever the server returns.
  onSearch?: (term: string) => void;
  // When set, the search term is remembered (sessionStorage) under this key, so
  // searching, opening a result, and coming back keeps the search instead of
  // clearing it. Omit for an ephemeral table.
  storageKey?: string;
}

export function DataTable<TData>({
  data,
  columns,
  searchPlaceholder = 'Search records...',
  exportFilename = 'export.csv',
  pageSize = 10,
  actions,
  filterComponent,
  onSearch,
  storageKey,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = usePersistentState<string>(storageKey ? `${storageKey}.q` : '', '');

  // Debounce the backend search so we don't fire a request per keystroke.
  const searchTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (term: string) => {
    setGlobalFilter(term);
    if (!onSearch) return;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => onSearch(term), 300);
  };

  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
    },
    // Forgiving search: ignore case, spaces and punctuation on BOTH sides, so
    // "prime excellence" matches "primeexcellencedaycareschool@…" and a phone
    // typed with spaces still matches. Runs per globally-filterable column; the
    // row passes if any column matches.
    globalFilterFn: (row, columnId, filterValue) => {
      const norm = (v: unknown) => String(v ?? '').toLowerCase().replace(/[^a-z0-9]+/g, '');
      const term = norm(filterValue);
      if (!term) return true;
      return norm(row.getValue(columnId)).includes(term);
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize,
      },
    },
  });

  const exportFilteredCsv = () => {
    const rows = table.getFilteredRowModel().rows;
    if (rows.length === 0) return;

    // Extract headers from visible leaf columns
    const headers = table
      .getVisibleLeafColumns()
      .filter((col) => col.id !== 'actions')
      .map((col) => {
        const header = col.columnDef.header;
        return typeof header === 'string' ? header : col.id;
      });

    const csvContent = [
      headers.join(','),
      ...rows.map((row) =>
        table
          .getVisibleLeafColumns()
          .filter((col) => col.id !== 'actions')
          .map((col) => {
            const val = row.getValue(col.id);
            const strVal = val !== undefined && val !== null ? String(val) : '';
            return `"${strVal.replace(/"/g, '""')}"`;
          })
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', exportFilename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="table-container">
      <div className="table-toolbar">
        <div className="table-toolbar-left">
          <div className="search-input-wrapper">
            <Search size={15} />
            <input
              type="text"
              className="input"
              value={globalFilter ?? ''}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={searchPlaceholder}
            />
          </div>
          {filterComponent}
        </div>
        <div className="table-toolbar-right">
          <button
            onClick={exportFilteredCsv}
            className="btn btn-secondary btn-sm"
            title="Export filtered rows to CSV"
          >
            <Download size={14} />
            <span>Export CSV</span>
          </button>
          {actions}
        </div>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table className="data-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  const isSortable = header.column.getCanSort();
                  const sortDirection = header.column.getIsSorted();

                  return (
                    <th
                      key={header.id}
                      className={isSortable ? 'sortable' : ''}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {isSortable && (
                          <span style={{ opacity: sortDirection ? 1 : 0.4 }}>
                            {sortDirection === 'asc' ? (
                              <ArrowUp size={13} />
                            ) : sortDirection === 'desc' ? (
                              <ArrowDown size={13} />
                            ) : (
                              <ArrowUpDown size={13} />
                            )}
                          </span>
                        )}
                      </div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  style={{ textAlign: 'center', padding: '36px', color: 'var(--text-dim)' }}
                >
                  No matching records found.
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div>
          Showing{' '}
          <span style={{ color: 'var(--text-main)', fontWeight: 600 }}>
            {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}
          </span>{' '}
          to{' '}
          <span style={{ color: 'var(--text-main)', fontWeight: 600 }}>
            {Math.min(
              (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
              table.getFilteredRowModel().rows.length
            )}
          </span>{' '}
          of{' '}
          <span style={{ color: 'var(--text-main)', fontWeight: 600 }}>
            {table.getFilteredRowModel().rows.length}
          </span>{' '}
          records
        </div>

        <div className="pagination-controls">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          >
            <ChevronLeft size={14} />
            <span>Prev</span>
          </button>
          <span style={{ margin: '0 8px', fontSize: '12px' }}>
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount() || 1}
          </span>
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          >
            <span>Next</span>
            <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
